import { colToLetter } from '../utils/columnUtils'

export type SpreadsheetSelectionRange = {
  col: number
  row: number
  endCol: number
  endRow: number
}

const CELL_REF_RE = /^\$?([A-Z]+)\$?([1-9]\d*)$/i
const COL_REF_RE = /^\$?([A-Z]+)$/i
const ROW_REF_RE = /^\$?([1-9]\d*)$/i

export function letterToCol(label: string): number | null {
  const text = String(label || '').trim().toUpperCase()
  if (!/^[A-Z]+$/.test(text)) return null
  let value = 0
  for (let i = 0; i < text.length; i++) {
    value = value * 26 + (text.charCodeAt(i) - 64)
  }
  return value > 0 ? value - 1 : null
}

export function formatCellAddress(col: number, row: number): string {
  const safeCol = Math.max(0, Math.floor(Number(col) || 0))
  const safeRow = Math.max(0, Math.floor(Number(row) || 0))
  return `${colToLetter(safeCol)}${safeRow + 1}`
}

export function normalizeSelectionRange(selection: SpreadsheetSelectionRange): SpreadsheetSelectionRange {
  const startCol = Math.max(0, Math.min(selection.col, selection.endCol))
  const endCol = Math.max(0, Math.max(selection.col, selection.endCol))
  const startRow = Math.max(0, Math.min(selection.row, selection.endRow))
  const endRow = Math.max(0, Math.max(selection.row, selection.endRow))
  return {
    col: startCol,
    row: startRow,
    endCol,
    endRow,
  }
}

export function formatSelectionAddress(
  selection: SpreadsheetSelectionRange | null | undefined,
  sheetSize?: { colCount?: number; rowCount?: number },
): string {
  if (!selection) return 'A1'
  const normalized = normalizeSelectionRange(selection)
  const colCount = Math.max(1, Math.floor(Number(sheetSize?.colCount) || 0))
  const rowCount = Math.max(1, Math.floor(Number(sheetSize?.rowCount) || 0))
  const fullRows = colCount > 0 && normalized.col === 0 && normalized.endCol >= colCount - 1
  const fullCols = rowCount > 0 && normalized.row === 0 && normalized.endRow >= rowCount - 1
  if (fullRows && normalized.row !== normalized.endRow) {
    return `${normalized.row + 1}:${normalized.endRow + 1}`
  }
  if (fullRows) return `${normalized.row + 1}:${normalized.row + 1}`
  if (fullCols && normalized.col !== normalized.endCol) {
    return `${colToLetter(normalized.col)}:${colToLetter(normalized.endCol)}`
  }
  if (fullCols) return `${colToLetter(normalized.col)}:${colToLetter(normalized.col)}`
  const start = formatCellAddress(normalized.col, normalized.row)
  const end = formatCellAddress(normalized.endCol, normalized.endRow)
  return start === end ? start : `${start}:${end}`
}

function parseCellRef(raw: string): { col: number; row: number } | null {
  const match = String(raw || '').trim().match(CELL_REF_RE)
  if (!match) return null
  const col = letterToCol(match[1] || '')
  const row = Number(match[2]) - 1
  if (col == null || !Number.isFinite(row) || row < 0) return null
  return { col, row }
}

function clampSelection(
  selection: SpreadsheetSelectionRange,
  sheetSize?: { colCount?: number; rowCount?: number },
): SpreadsheetSelectionRange {
  const maxCol = Math.max(0, Math.floor(Number(sheetSize?.colCount) || 16_384) - 1)
  const maxRow = Math.max(0, Math.floor(Number(sheetSize?.rowCount) || 1_048_576) - 1)
  return {
    col: Math.min(Math.max(0, selection.col), maxCol),
    row: Math.min(Math.max(0, selection.row), maxRow),
    endCol: Math.min(Math.max(0, selection.endCol), maxCol),
    endRow: Math.min(Math.max(0, selection.endRow), maxRow),
  }
}

export function parseA1Selection(
  input: string,
  sheetSize?: { colCount?: number; rowCount?: number },
): SpreadsheetSelectionRange | null {
  const text = String(input || '')
    .trim()
    .replace(/^=/, '')
    .replace(/\s+/g, '')
  if (!text) return null
  const withoutSheet = text.includes('!') ? text.slice(text.lastIndexOf('!') + 1) : text
  const parts = withoutSheet.split(':')
  if (parts.length > 2 || parts.some((part) => !part)) return null

  const firstCell = parseCellRef(parts[0] || '')
  const secondCell = parts[1] ? parseCellRef(parts[1]) : null
  if (firstCell && (!parts[1] || secondCell)) {
    return clampSelection({
      col: firstCell.col,
      row: firstCell.row,
      endCol: secondCell?.col ?? firstCell.col,
      endRow: secondCell?.row ?? firstCell.row,
    }, sheetSize)
  }

  if (parts.length === 2) {
    const firstCol = String(parts[0] || '').match(COL_REF_RE)
    const secondCol = String(parts[1] || '').match(COL_REF_RE)
    if (firstCol && secondCol) {
      const col = letterToCol(firstCol[1] || '')
      const endCol = letterToCol(secondCol[1] || '')
      if (col == null || endCol == null) return null
      const maxRow = Math.max(0, Math.floor(Number(sheetSize?.rowCount) || 1_048_576) - 1)
      return clampSelection({ col, row: 0, endCol, endRow: maxRow }, sheetSize)
    }

    const firstRow = String(parts[0] || '').match(ROW_REF_RE)
    const secondRow = String(parts[1] || '').match(ROW_REF_RE)
    if (firstRow && secondRow) {
      const row = Number(firstRow[1]) - 1
      const endRow = Number(secondRow[1]) - 1
      const maxCol = Math.max(0, Math.floor(Number(sheetSize?.colCount) || 16_384) - 1)
      return clampSelection({ col: 0, row, endCol: maxCol, endRow }, sheetSize)
    }
  }

  return null
}
